import { Injectable } from '@angular/core';
import type { BackendPushResponse } from '@freelancer/datastore';
import { Datastore } from '@freelancer/datastore';
import type {
  DiscoverCollectionItemsCollection,
  DiscoverItemContext,
  PredefinedDiscoverCollectionsCollection,
  RecentlyViewedCollectionTypes,
} from '@freelancer/datastore/collections';
import { isDefined } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { of, firstValueFrom } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
/**
 * Used to create and update recently-viewed discover collections.
 *
 * Recently viewed discover collections are used to track users, projects etc. that have recently been viewed by the
 * collection's owner and display them in a list. It essentially provides them with a history of different entities
 * they have viewed on the freelancer site.
 *
 * @see RecentlyViewedCollectionTypes
 *
 * @usage
 * Example usage: Add a user to the Recently Viewed Users collection whenever UserProfileComponent loads.
 * ```
 * ngOnInit{
 *    this.recentlyViewedService.addToRecentlyViewedCollection(
 *           DiscoverCollectionTypeApi.LISTS_RECENTLY_VIEWED_USERS,
 *           {
 *             itemId: userId,
 *             type: DiscoverCollectionItemTypeApi.USER,
 *           },
 *         );
 * }
 * ```
 */
export class RecentlyViewedService {
  constructor(private datastore: Datastore) {}

  addToRecentlyViewedCollection(
    collectionType: RecentlyViewedCollectionTypes,
    context: DiscoverItemContext,
  ): Promise<BackendPushResponse<DiscoverCollectionItemsCollection>> {
    // Fetch the Recently Viewed Collection. If it doesn't exist yet it will be created
    const recentlyViewedListDocument =
      this.datastore.document<PredefinedDiscoverCollectionsCollection>(
        'predefinedDiscoverCollections',
        collectionType,
        { index: 'type' },
      );

    return firstValueFrom(
      recentlyViewedListDocument.status$.pipe(
        switchMap(status => {
          return status.error
            ? of(undefined)
            : recentlyViewedListDocument.valueChanges();
        }),
        // We only want to take the first value, and only after the request succeeds
        filter(isDefined),
        untilDestroyed(this),
      ),
    ).then(recentlyViewedList =>
      this.datastore.createDocument<DiscoverCollectionItemsCollection>(
        'discoverCollectionItems',
        {
          collectionId: recentlyViewedList.id,
          context,
          isDeleted: false,
        },
      ),
    );
  }
}
